import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHead from './../general/PageHead';
import { Skeleton } from './../general/Skeleton';
import { useAuth } from './../general/AuthContext';
import { authStorage } from '../../utils/storage';
import '../styles/staff/Staff.css';

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase();
}

function duesColor(p) {
  if (p >= 85) return 'var(--color-success, #16a34a)';
  if (p >= 70) return 'var(--color-warning, #d97706)';
  return 'var(--color-danger, #dc2626)';
}

function StaffColumn({ title, data, loading, dues }) {
  return (
    <div className="card">
      <div className="card-head" style={{ padding: '16px 20px' }}>
        <h3 style={{ fontSize: 14 }}>{title}</h3>
        {!loading && <span className="badge badge-neutral" style={{ marginLeft: 'auto' }}>{data.length}</span>}
      </div>
      <div className="coord-list" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        {loading ? (
          [...Array(5)].map((_, i) => (
            <div key={i} className="item">
              <Skeleton width="140px" height="14px" />
            </div>
          ))
        ) : (
          data.map((s, i) => (
            <Link key={i} to={`/employee/${s.EmployeeId}`} className="item" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="avatar-mini">{initials(s.full_name)}</div>
              <span style={{ fontSize: 13.5, color: 'var(--fg-1)' }}>{s.full_name}</span>
              {dues && dues[s.EmployeeId] != null && (
                <span title="Dues-paying rate across this rep's units" style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: duesColor(dues[s.EmployeeId]) }}>{dues[s.EmployeeId]}%</span>
              )}
            </Link>
          ))
        )}
      </div>
    </div>
  );
}

function Staff() {
  const navigate = useNavigate();
  const { auth, setAuth } = useAuth();
  const [staffReps, setStaffReps] = useState([]);
  const [serviceReps, setServiceReps] = useState([]);
  const [fieldReps, setFieldReps] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [dues, setDues] = useState({}); // EmployeeId -> dues-paying %
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth.isAuthenticated) { navigate('/login'); return; }
    const token = authStorage.getToken();
    if (!token) { navigate('/login'); return; }

    setLoading(true);
    Promise.all(
      ['/api/staff-reps', '/api/service-reps', '/api/field-reps', '/api/organizers', '/api/staff-dues'].map(ep =>
        fetch(ep, { headers: { Authorization: `Bearer ${auth.token}` } })
          .then(r => { if (r.status === 401) throw new Error('Unauthorized'); return r.json(); })
      )
    )
      .then(([sr, svc, fr, org, duesRows]) => {
        setStaffReps(sr);
        setServiceReps(svc);
        setFieldReps(fr);
        setOrganizers(org);
        const m = {};
        (Array.isArray(duesRows) ? duesRows : []).forEach(d => { if (d && d.EmployeeId != null && d.pct != null) m[d.EmployeeId] = d.pct; });
        setDues(m);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        if (err.message === 'Unauthorized') { setAuth({ isAuthenticated: false, token: null }); navigate('/login'); }
        setLoading(false);
      });
  }, [auth, navigate, setAuth]);

  return (
    <>
      <PageHead title="Staff Directory" sub="Council 4 representatives and organizers" />
      <div className="grid-4 staff-grid">
        <StaffColumn title="Staff Representatives"   data={staffReps}   loading={loading} dues={dues} />
        <StaffColumn title="Service Representatives" data={serviceReps} loading={loading} dues={dues} />
        <StaffColumn title="Field Representatives"  data={fieldReps}   loading={loading} dues={dues} />
        <StaffColumn title="Organizers"              data={organizers}  loading={loading} dues={dues} />
      </div>
    </>
  );
}

export default Staff;
